import { addDays, format, parse, subDays } from "date-fns";
import React, { useState, useEffect, useRef } from "react";
import DayPicker, { DateUtils, RangeModifier } from "react-day-picker";
import "react-day-picker/lib/style.css";
import "./App.scss";
import { SelectedDatePanel } from "./Components/SelectedDatePanel";
import { useEventDates } from "./Hooks/useEventDates";
import { getSelectedAndAvailableDates } from "./utils";

/**
 * Get buttonHref value from data attribute on root element
 */
function useButtonHref() {
  const [href, setHref] = useState("");
  useEffect(() => {
    const foundHref = document
      .getElementById("ganjier-calendar")
      ?.getAttribute("data-buttonHref");
    foundHref && setHref(foundHref);
  }, []);
  return href;
}

function App({
  inputEl,
  type,
}: {
  inputEl?: HTMLInputElement;
  type?: "live_event" | "exam";
}) {
  const [show, setShow] = useState(!Boolean(inputEl));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!inputEl) return;
    inputEl.addEventListener("focusin", () => {
      setShow(true);

      function closeOnClickOutside(event: MouseEvent) {
        if (
          !ref.current?.contains(event.target as Node) &&
          !inputEl!.contains(event.target as Node)
        ) {
          setShow(false);
          document.removeEventListener("click", closeOnClickOutside);
        }
      }
      document.addEventListener("click", closeOnClickOutside);
    });
  }, [inputEl]);

  const buttonHref = useButtonHref();
  const dates = useEventDates(type);
  const [selectedDates, setSelectedDates] = useState<RangeModifier>({
    from: undefined,
    to: undefined,
  });

  if (!show) {
    return null;
  }

  const { liveEventSecondDays, availableDates, soldOutDates } =
    getSelectedAndAvailableDates(dates);
  const selectedDate = selectedDates.from;
  const isSelectedAvailable = Boolean(
    selectedDate &&
      availableDates.find(([date]) => DateUtils.isSameDay(date, selectedDate)),
  );
  const isSelectedSoldOut = Boolean(
    selectedDate &&
      soldOutDates.find(([date]) => DateUtils.isSameDay(date, selectedDate)),
  );
  let initialDate = new Date();
  initialDate.setMonth(3);
  initialDate.setFullYear(2025);

  return (
    <div
      ref={ref}
      className={`GanjierCalendar ${inputEl ? "GanjierCalendar--input" : ""}`}
    >
      <DayPicker
        month={initialDate}
        modifiers={{
          available: availableDates.map(([date]) => date),
          soldOut: soldOutDates.map(([date]) => date),
        }}
        onDayClick={(clickedDay, { selected, disabled }) => {
          let selectedDates: RangeModifier = { from: undefined, to: undefined };
          if (selected || disabled) {
            setSelectedDates(selectedDates);
            return;
          }
          selectedDates = DateUtils.addDayToRange(clickedDay, selectedDates);
          if (
            Object.entries(dates.liveEvents).find(([date]) =>
              DateUtils.isSameDay(
                parse(date, "MM/dd/yyyy", new Date()),
                clickedDay,
              ),
            )
          ) {
            selectedDates = DateUtils.addDayToRange(
              addDays(clickedDay, 1),
              selectedDates,
            );
          }
          if (
            liveEventSecondDays.find(([date]) =>
              DateUtils.isSameDay(
                parse(date, "MM/dd/yyyy", new Date()),
                clickedDay,
              ),
            )
          ) {
            selectedDates = DateUtils.addDayToRange(
              subDays(clickedDay, 1),
              selectedDates,
            );
          }
          setSelectedDates(selectedDates);
        }}
        disabledDays={(day) =>
          ![...availableDates, ...soldOutDates].find(([date]) =>
            DateUtils.isSameDay(day, date),
          )
        }
        selectedDays={[selectedDates.from || undefined, selectedDates]}
        showOutsideDays
      />
      <div
        className={`GanjierCalendar-description ${
          isSelectedAvailable && "GanjierCalendar-description--available"
        } ${isSelectedSoldOut && "GanjierCalendar-description--soldOut"}`}
      >
        {!selectedDates.from ? (
          <p className="GanjierCalendar-Title GanjierCalendar-Title--unselected">
            Please select a day
          </p>
        ) : (
          <SelectedDatePanel
            onConfirm={() => {
              if (inputEl) {
                if (selectedDates.from) {
                  inputEl.value = format(selectedDates.from, "MM/dd/yyyy");
                  setShow(false);
                }
              } else {
                window.location.href = buttonHref;
              }
            }}
            selectedDates={selectedDates as RangeModifier & { from: Date }}
            dates={dates}
          />
        )}
      </div>
    </div>
  );
}

export default App;
